/* Honey Girl Organics — Golden PDP interactions */

let HorizonEvents = null;

// Horizon exposes its cart events through an import map. Load that integration
// opportunistically, but never hold the gallery, variants, or cart controls
// behind the import. The PDP remains fully interactive when the import map is
// unavailable (including some theme-editor preview contexts).
import('@shopify/events')
  .then((events) => {
    HorizonEvents = events;
  })
  .catch(() => {
    HorizonEvents = null;
  });

document.documentElement.classList.add('hgo-pdp-script-loaded');

const ROOT_SELECTOR = '[data-hgo-pdp-root]';

function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
  if (!Number.isFinite(number)) return '0';
  const fixed = number.toFixed(precision);
  const parts = fixed.split('.');
  const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
  return precision ? dollars + decimal + parts[1] : dollars;
}

function formatMoney(cents, moneyFormat) {
  const format = moneyFormat || ('$' + '{{amount}}');
  const tokenMatch = format.match(/{{\s*(amount[^}]*)\s*}}/);
  if (!tokenMatch) return '$' + formatWithDelimiters(cents / 100);

  const token = tokenMatch[1].trim();
  let value;

  switch (token) {
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents / 100, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents / 100, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents / 100, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents / 100, 2, "'", '.');
      break;
    default:
      value = formatWithDelimiters(cents / 100, 2);
  }

  return format.replace(tokenMatch[0], value);
}

function parseResponse(response) {
  return response
    .json()
    .catch(() => ({}))
    .then((json) => {
      if (!response.ok || json.status) {
        const message = json.description || json.message || 'Could not add this item to the cart.';
        const error = new Error(message);
        error.detail = json;
        throw error;
      }
      return json;
    });
}

function getRoots(context = document) {
  const roots = [];
  if (context instanceof Element && context.matches(ROOT_SELECTOR)) roots.push(context);
  if (context.querySelectorAll) roots.push(...context.querySelectorAll(ROOT_SELECTOR));
  return roots;
}

function initPdp(root) {
  if (root.dataset.hgoInitialized === 'true') return;

  const dataElement = root.querySelector('[data-hgo-pdp-data]');
  if (!dataElement) return;

  let data;
  try {
    data = JSON.parse(dataElement.textContent);
  } catch (error) {
    root.dataset.hgoInitializationError = 'invalid-product-data';
    console.error('[HGO PDP] Product data could not be parsed.', error);
    return;
  }

  root.dataset.hgoInitialized = 'true';
  document.documentElement.classList.add('hgo-js');

  const abortController = new AbortController();
  const signal = abortController.signal;
  const observers = [];
  root.hgoDestroy = () => {
    abortController.abort();
    observers.forEach((observer) => observer.disconnect());
    delete root.hgoDestroy;
    delete root.dataset.hgoInitialized;
  };

  const select = (selector) => root.querySelector(selector);
  const selectAll = (selector) => Array.from(root.querySelectorAll(selector));
  const listen = (element, type, callback, options = {}) => {
    if (!element) return;
    element.addEventListener(type, callback, { ...options, signal });
  };

  const form = select('[data-hgo-atc-form]');
  const variantInput = select('[data-hgo-variant-id]');
  const quantityInput = select('[data-hgo-qty-input]');
  const quantityOutput = select('[data-hgo-qty-output]');
  const quantityMinus = select('[data-hgo-qty-minus]');
  const quantityPlus = select('[data-hgo-qty-plus]');
  const addButton = select('[data-hgo-atc-button]');
  const addLabel = select('[data-hgo-atc-label]');
  const priceNow = select('[data-hgo-price-now]');
  const priceCompare = select('[data-hgo-price-compare]');
  const priceNote = select('[data-hgo-price-note]');
  const oneTimeRadio = select('[data-hgo-purchase-onetime]');
  const subscriptionRadio = select('[data-hgo-purchase-subscription]');
  const subscriptionLabel = select('[data-hgo-subscription-label]');
  const planSelect = select('[data-hgo-plan-select]');
  const sellingPlanInput = select('[data-hgo-selling-plan]');
  const stickyBar = select('[data-hgo-sticky-atc]');
  const stickyButton = select('[data-hgo-sticky-button]');
  const stickyPrice = select('[data-hgo-sticky-price]');
  const stickyImage = select('[data-hgo-sticky-image]');
  const toast = select('[data-hgo-toast]');
  const buybox = select('[data-hgo-buybox]');

  const money = (cents) => formatMoney(Number(cents) || 0, data.moneyFormat);
  let currentVariant =
    data.variants.find((variant) => Number(variant.id) === Number(data.selectedVariantId)) ||
    data.variants[0] ||
    null;
  let selectedOptions = currentVariant ? currentVariant.options.slice() : [];
  let cartBusy = false;

  function showToast(message, isError = false) {
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    toast.classList.toggle('is-error', isError);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => {
        toast.hidden = true;
      }, 280);
    }, isError ? 4200 : 2600);
  }

  function findVariant(options = selectedOptions) {
    return (
      data.variants.find(
        (variant) =>
          variant.options.length === options.length &&
          variant.options.every((option, index) => option === options[index])
      ) || null
    );
  }

  function findAllocation(variant, planId) {
    if (!variant || !planId) return null;
    return (
      variant.sellingPlanAllocations.find(
        (allocation) => Number(allocation.sellingPlanId) === Number(planId)
      ) || null
    );
  }

  function selectedPlanId() {
    if (!subscriptionRadio?.checked || !planSelect?.value) return null;
    return Number(planSelect.value);
  }

  function selectedPlan() {
    const planId = selectedPlanId();
    if (!planId) return null;
    return data.sellingPlans.find((plan) => Number(plan.id) === planId) || null;
  }

  function pricingState() {
    const planId = selectedPlanId();
    const allocation = findAllocation(currentVariant, planId);

    if (allocation) {
      return {
        priceCents: Number(allocation.priceCents),
        compareAtCents: Number(allocation.compareAtCents) || Number(currentVariant.priceCents),
        allocation,
        plan: selectedPlan(),
      };
    }

    return {
      priceCents: Number(currentVariant?.priceCents || 0),
      compareAtCents: Number(currentVariant?.compareAtCents || 0),
      allocation: null,
      plan: null,
    };
  }

  function syncPlanAvailability() {
    if (!planSelect || !subscriptionRadio || !currentVariant) return;

    const options = Array.from(planSelect.options);
    options.forEach((option) => {
      option.disabled = !findAllocation(currentVariant, Number(option.value));
    });

    const availableOptions = options.filter((option) => !option.disabled);
    const selectedOption = options.find((option) => option.selected);

    if (!selectedOption || selectedOption.disabled) {
      if (availableOptions.length) {
        availableOptions[0].selected = true;
      } else if (oneTimeRadio) {
        oneTimeRadio.checked = true;
      }
    }

    const hasSubscription = availableOptions.length > 0;
    subscriptionRadio.disabled = !hasSubscription;
    planSelect.disabled = !hasSubscription;
    subscriptionLabel?.classList.toggle('is-disabled', !hasSubscription);

    if (!hasSubscription && sellingPlanInput) {
      sellingPlanInput.disabled = true;
      sellingPlanInput.value = '';
    }
  }

  function updateQuantityRule() {
    if (!currentVariant || !quantityInput || !quantityOutput) return;
    const rule = currentVariant.quantityRule || { min: 1, max: 0, increment: 1 };
    const min = Math.max(1, Number(rule.min) || 1);
    const increment = Math.max(1, Number(rule.increment) || 1);
    const current = Math.max(min, Number(quantityInput.value) || min);
    const normalized = min + Math.max(0, Math.round((current - min) / increment)) * increment;
    const max = Number(rule.max) || Infinity;
    const next = Math.min(normalized, max);
    quantityInput.value = String(next);
    quantityOutput.textContent = String(next);
  }

  function setQuantity(candidate) {
    if (!currentVariant || !quantityInput || !quantityOutput) return;
    const rule = currentVariant.quantityRule || { min: 1, max: 0, increment: 1 };
    const min = Math.max(1, Number(rule.min) || 1);
    const max = Number(rule.max) || Infinity;
    const next = Math.max(min, Math.min(max, candidate));
    quantityInput.value = String(next);
    quantityOutput.textContent = String(next);
  }

  function renderOptionAvailability() {
    selectAll('[data-hgo-option]').forEach((button) => {
      const optionIndex = Number(button.dataset.optionIndex);
      const candidateOptions = selectedOptions.slice();
      candidateOptions[optionIndex] = button.dataset.value;
      const exists = data.variants.some(
        (variant) =>
          variant.options.length === candidateOptions.length &&
          variant.options.every((option, index) => option === candidateOptions[index])
      );
      button.setAttribute('aria-disabled', exists ? 'false' : 'true');
      button.disabled = !exists;
      const active = candidateOptions[optionIndex] === selectedOptions[optionIndex];
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-checked', active ? 'true' : 'false');
    });
  }

  function pauseMedia(slide) {
    slide.querySelectorAll('video, audio').forEach((media) => {
      if (typeof media.pause === 'function') media.pause();
    });

    slide.querySelectorAll('iframe').forEach((frame) => {
      try {
        frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo' }), '*');
        frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
      } catch (error) {
        // Cross-origin media can safely ignore pause messages.
      }
    });

    slide.querySelectorAll('model-viewer').forEach((model) => {
      if (typeof model.pause === 'function') model.pause();
    });
  }

  function activateMedia(mediaId, focusThumb = false) {
    if (!mediaId) return;
    const id = String(mediaId);
    const slides = selectAll('[data-hgo-media-slide]');
    const thumbs = selectAll('[data-hgo-media-thumb]');
    const targetSlide = slides.find((slide) => slide.dataset.mediaId === id);

    if (!targetSlide) return;

    slides.forEach((slide) => {
      const active = slide === targetSlide;
      slide.hidden = !active;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (!active) pauseMedia(slide);
    });

    thumbs.forEach((thumb) => {
      const active = thumb.dataset.mediaId === id;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-selected', active ? 'true' : 'false');
      thumb.tabIndex = active ? 0 : -1;
      if (active && focusThumb) thumb.focus({ preventScroll: true });
      if (active) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });

    const activeIndex = thumbs.findIndex((thumb) => thumb.dataset.mediaId === id);
    root.querySelectorAll('[data-hgo-gallery-position]').forEach((position) => {
      position.textContent = activeIndex >= 0 ? `${activeIndex + 1} / ${thumbs.length}` : '';
    });
  }

  function updateUrl() {
    if (!currentVariant || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', currentVariant.id);
    window.history.replaceState({ ...window.history.state, variantId: currentVariant.id }, '', url);
  }

  function effectiveRoutineItems(button) {
    let items;
    try {
      items = JSON.parse(button.dataset.routineItems || '[]');
    } catch (error) {
      return [];
    }

    return items.map((item) => {
      if (Number(item.productId) === Number(data.productId) && currentVariant) {
        return {
          ...item,
          variantId: Number(currentVariant.id),
          priceCents: Number(currentVariant.priceCents),
          available: Boolean(currentVariant.available),
        };
      }
      return {
        ...item,
        variantId: Number(item.variantId),
        priceCents: Number(item.priceCents),
        available: Boolean(item.available),
      };
    });
  }

  function renderRoutineButtons() {
    selectAll('[data-hgo-routine-add]').forEach((button) => {
      const items = effectiveRoutineItems(button);
      const label = button.querySelector('[data-hgo-routine-label]');
      const total = items.reduce((sum, item) => sum + item.priceCents, 0);
      const allAvailable = items.length > 0 && items.every((item) => item.available);
      button.disabled = cartBusy || !allAvailable;
      button.dataset.effectiveItems = JSON.stringify(items);
      if (label) label.textContent = 'Add the ' + items.length + '-step ritual — ' + money(total);
    });
  }

  function renderPurchaseState() {
    if (!currentVariant) return;
    syncPlanAvailability();

    const state = pricingState();
    const subscriptionSelected = Boolean(subscriptionRadio?.checked);
    const hasValidAllocation = !subscriptionSelected || Boolean(state.allocation);
    const available = Boolean(currentVariant.available && hasValidAllocation);

    if (variantInput) variantInput.value = currentVariant.id;
    if (priceNow) priceNow.textContent = money(state.priceCents);
    if (priceCompare) {
      const showCompare = state.compareAtCents > state.priceCents;
      priceCompare.hidden = !showCompare;
      priceCompare.textContent = showCompare ? money(state.compareAtCents) : '';
    }
    if (priceNote) {
      priceNote.textContent = state.plan ? 'Subscription · ' + state.plan.name : 'One-time purchase';
    }
    if (sellingPlanInput) {
      sellingPlanInput.value = state.plan ? String(state.plan.id) : '';
      sellingPlanInput.disabled = !state.plan;
    }
    if (addLabel) {
      addLabel.textContent = available ? 'Add to cart — ' + money(state.priceCents) : 'Sold out';
    }
    if (addButton) addButton.disabled = cartBusy || !available;
    if (stickyButton) stickyButton.disabled = cartBusy || !available;
    if (stickyPrice) stickyPrice.textContent = money(state.priceCents);
    if (stickyImage && currentVariant.featuredImage) stickyImage.src = currentVariant.featuredImage;

    updateQuantityRule();
    renderOptionAvailability();
    renderRoutineButtons();
  }

  function selectVariant(variant, { updateHistory = true } = {}) {
    if (!variant) return;
    currentVariant = variant;
    selectedOptions = variant.options.slice();
    if (variant.featuredMediaId) activateMedia(variant.featuredMediaId);
    if (updateHistory) updateUrl();
    renderPurchaseState();
    root.dispatchEvent(
      new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant, productId: data.productId },
      })
    );
  }

  selectAll('[data-hgo-option]').forEach((button) => {
    listen(button, 'click', () => {
      if (button.getAttribute('aria-disabled') === 'true') return;
      const optionIndex = Number(button.dataset.optionIndex);
      selectedOptions[optionIndex] = button.dataset.value;
      const variant = findVariant();
      if (variant) selectVariant(variant);
    });
  });

  listen(quantityMinus, 'click', () => {
    const increment = Number(currentVariant?.quantityRule?.increment) || 1;
    setQuantity(Number(quantityInput?.value || 1) - increment);
  });

  listen(quantityPlus, 'click', () => {
    const increment = Number(currentVariant?.quantityRule?.increment) || 1;
    setQuantity(Number(quantityInput?.value || 1) + increment);
  });

  listen(oneTimeRadio, 'change', renderPurchaseState);
  listen(subscriptionRadio, 'change', renderPurchaseState);
  listen(planSelect, 'change', () => {
    if (subscriptionRadio) subscriptionRadio.checked = true;
    renderPurchaseState();
  });

  const mediaThumbs = selectAll('[data-hgo-media-thumb]');
  const galleryPrevious = select('[data-hgo-gallery-prev]');
  const galleryNext = select('[data-hgo-gallery-next]');

  function moveMedia(direction) {
    if (mediaThumbs.length < 2) return;
    const activeIndex = mediaThumbs.findIndex((thumb) => thumb.getAttribute('aria-selected') === 'true');
    const currentIndex = activeIndex >= 0 ? activeIndex : 0;
    const nextIndex = (currentIndex + direction + mediaThumbs.length) % mediaThumbs.length;
    activateMedia(mediaThumbs[nextIndex].dataset.mediaId, true);
  }

  mediaThumbs.forEach((thumb, index) => {
    listen(thumb, 'click', () => activateMedia(thumb.dataset.mediaId));
    listen(thumb, 'keydown', (event) => {
      let nextIndex = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % mediaThumbs.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + mediaThumbs.length) % mediaThumbs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = mediaThumbs.length - 1;
      if (nextIndex === null) return;
      event.preventDefault();
      activateMedia(mediaThumbs[nextIndex].dataset.mediaId, true);
    });
  });

  const initiallySelectedMedia = mediaThumbs.find(
    (thumb) => thumb.getAttribute('aria-selected') === 'true'
  );
  if (initiallySelectedMedia) activateMedia(initiallySelectedMedia.dataset.mediaId);

  listen(galleryPrevious, 'click', () => moveMedia(-1));
  listen(galleryNext, 'click', () => moveMedia(1));

  selectAll('[data-hgo-routine-tab]').forEach((tab) => {
    listen(tab, 'click', () => {
      const target = tab.dataset.hgoRoutineTab;
      selectAll('[data-hgo-routine-tab]').forEach((candidate) => {
        const active = candidate === tab;
        candidate.classList.toggle('is-active', active);
        candidate.setAttribute('aria-selected', active ? 'true' : 'false');
        candidate.tabIndex = active ? 0 : -1;
      });
      selectAll('[data-hgo-routine-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.hgoRoutinePanel !== target;
      });
    });
  });

  function cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
  }

  function beginCartEvent(items) {
    const EventClass = HorizonEvents?.CartLinesUpdateEvent;
    if (!EventClass || typeof EventClass.createPromise !== 'function') return null;

    try {
      const deferred = EventClass.createPromise();
      root.dispatchEvent(
        new EventClass({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({
            merchandiseId: String(item.id),
            quantity: Number(item.quantity),
          })),
          promise: deferred.promise,
        })
      );
      return deferred;
    } catch (error) {
      console.warn('[HGO PDP] Horizon cart event unavailable; using section refresh.', error);
      return null;
    }
  }

  function dispatchCartError(error) {
    const ErrorClass = HorizonEvents?.CartErrorEvent;
    if (!ErrorClass) return;
    try {
      root.dispatchEvent(
        new ErrorClass({
          error: error.message || 'Add to cart failed',
          code: error.detail?.status || 'INVALID',
          detail: error.detail || {},
        })
      );
    } catch (eventError) {
      console.warn('[HGO PDP] Horizon cart error event unavailable.', eventError);
    }
  }

  function replaceSections(sections) {
    if (!sections) return;
    Object.entries(sections).forEach(([sectionId, html]) => {
      if (!html) return;
      const current = document.getElementById('shopify-section-' + sectionId);
      if (!current) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement =
        parsed.getElementById('shopify-section-' + sectionId) || parsed.body.firstElementChild;
      if (replacement) current.replaceWith(replacement);
    });
  }

  function openCartDrawer() {
    const drawer = document.querySelector('theme-drawer#cart-drawer, #cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  async function fetchCart() {
    const response = await fetch(data.routes.cart, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal,
    });
    if (!response.ok) throw new Error('The cart updated, but its latest state could not be loaded.');
    return response.json();
  }

  async function performCartAdd({ body, headers }, eventItems) {
    const deferred = beginCartEvent(eventItems);

    try {
      const response = await fetch(data.routes.cartAdd, {
        method: 'POST',
        body,
        headers,
        credentials: 'same-origin',
        signal,
      }).then(parseResponse);

      const cart = await fetchCart();
      const detail = {
        items: cart.items,
        source: 'hgo-pdp',
        sourceId: root.id,
        itemCount: eventItems.reduce((sum, item) => sum + Number(item.quantity), 0),
        productId: data.productId,
        sections: response.sections,
        didError: false,
      };

      let horizonHandled = false;
      if (
        deferred &&
        typeof HorizonEvents?.CartLinesUpdateEvent?.createCartFromAjaxResponse === 'function'
      ) {
        try {
          deferred.resolve({
            cart: HorizonEvents.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail,
          });
          horizonHandled = true;
        } catch (error) {
          console.warn('[HGO PDP] Horizon cart refresh failed; using section refresh.', error);
        }
      }

      if (!horizonHandled) {
        replaceSections(response.sections);
        openCartDrawer();
      }

      document.dispatchEvent(new CustomEvent('hgo:cart-added', { detail: { cart, response } }));
      return { cart, response };
    } catch (error) {
      if (deferred) {
        try {
          deferred.reject(error);
        } catch (eventError) {
          console.warn('[HGO PDP] Horizon cart rejection failed.', eventError);
        }
      }
      dispatchCartError(error);
      throw error;
    }
  }

  async function addSingleProduct() {
    if (!form || !currentVariant || cartBusy) return;
    const state = pricingState();
    if (!currentVariant.available || (subscriptionRadio?.checked && !state.allocation)) return;

    cartBusy = true;
    renderPurchaseState();
    if (addLabel) addLabel.textContent = 'Adding…';

    const formData = new FormData(form);
    formData.delete('hgo_purchase_type');
    if (!state.plan) formData.delete('selling_plan');
    const sections = cartSectionIds();
    if (sections.length) formData.set('sections', sections.join(','));

    const eventItems = [
      {
        id: Number(currentVariant.id),
        quantity: Number(quantityInput?.value || 1),
      },
    ];

    try {
      await performCartAdd(
        {
          body: formData,
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        },
        eventItems
      );
      showToast(data.productTitle + ' added to cart.');
    } catch (error) {
      if (error.name !== 'AbortError') showToast(error.message, true);
    } finally {
      cartBusy = false;
      renderPurchaseState();
    }
  }

  listen(form, 'submit', (event) => {
    event.preventDefault();
    addSingleProduct();
  });
  listen(stickyButton, 'click', addSingleProduct);

  selectAll('[data-hgo-routine-add]').forEach((button) => {
    listen(button, 'click', async () => {
      if (cartBusy) return;
      const effectiveItems = effectiveRoutineItems(button);
      if (!effectiveItems.length || effectiveItems.some((item) => !item.available)) return;

      cartBusy = true;
      renderPurchaseState();
      const label = button.querySelector('[data-hgo-routine-label]');
      if (label) label.textContent = 'Adding ritual…';

      const eventItems = effectiveItems.map((item) => ({
        id: Number(item.variantId),
        quantity: 1,
      }));
      const payload = { items: eventItems };
      const sections = cartSectionIds();
      if (sections.length) payload.sections = sections.join(',');

      try {
        await performCartAdd(
          {
            body: JSON.stringify(payload),
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          },
          eventItems
        );
        showToast('Your HGO ritual was added to cart.');
      } catch (error) {
        if (error.name !== 'AbortError') showToast(error.message, true);
      } finally {
        cartBusy = false;
        renderPurchaseState();
      }
    });
  });

  if ('IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    observers.push(revealObserver);
    selectAll('.hgo-reveal').forEach((element) => revealObserver.observe(element));

    if (stickyBar && buybox) {
      let buyboxPassed = false;
      let footerVisible = false;
      const renderSticky = () => {
        const visible = buyboxPassed && !footerVisible;
        stickyBar.classList.toggle('is-visible', visible);
        stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      };

      const buyboxObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            buyboxPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 90;
            renderSticky();
          });
        },
        { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
      );
      observers.push(buyboxObserver);
      buyboxObserver.observe(buybox);

      const footer = document.querySelector('footer, .footer');
      if (footer) {
        const footerObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            footerVisible = entry.isIntersecting;
            renderSticky();
          });
        });
        observers.push(footerObserver);
        footerObserver.observe(footer);
      }
    }
  } else {
    selectAll('.hgo-reveal').forEach((element) => element.classList.add('is-in'));
  }

  renderPurchaseState();
}

function initAll(context = document) {
  getRoots(context).forEach((root) => {
    try {
      initPdp(root);
    } catch (error) {
      root.dataset.hgoInitializationError = 'unexpected-error';
      console.error('[HGO PDP] Initialization failed.', error);
    }
  });
}

initAll(document);

document.addEventListener('shopify:section:load', (event) => initAll(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  getRoots(event.target).forEach((root) => root.hgoDestroy?.());
});
